import ContentToggle from 'components/ContentToggle'
import FilterBar from 'components/FilterBar'
import Label from 'components/Label'
import Message from 'components/Message'
import UtilityBar from 'components/UtilityBar'
import { FetchContext } from 'contexts/FetchContext'
import { getEnrollmentList } from 'helpers/params'
import useApiParams from 'hooks/useApiParams'
import useDefaultParams from 'hooks/useDefaultParams'
import useLocalStorage from 'hooks/useLocalStorage'
import useScrollPosition from 'hooks/useScrollPosition'
import useSearch from 'hooks/useSearch'
import useUrlParams from 'hooks/useUrlParams'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LabelBadges from 'widgets/LabelBadges'
import Facets from 'widgets/PanelToggle/Facets'
import InPageNavPortal from '../portals/InPageNavPortal'
import LocalContentPortal from '../portals/LocalContentPortal'

function LocalPageTemplate() {
  const { t } = useTranslation()
  const { urlParamsChanged } = useDefaultParams()
  const [showDiv, setShowDiv] = useState(false)
  const [showUtilityBar, setUtilityBar] = useState(false)
  const { urlParams, updateUrlParams } = useUrlParams()
  const [storage, setStorage] = useLocalStorage('local_page', '')
  const { addApiParams } = useApiParams()
  const { resultList } = useContext(FetchContext)

  const toggleDiv = () => {
    setShowDiv(!showDiv)
  }
  useEffect(() => {
    Drupal.attachBehaviors()
  }, [resultList])

  /**
   * WK-7362
   * To always make sure we have enrolledIn data checked we have to make a diff check
   * against localStorage.apiParams.
   * This function is structured as it is to avoid reacts infinte loop, ie the function must always be
   * able to return eventually without calling any hooks.
   *
   * @param {*} enrolledIn
   * @returns
   */
  const setInitialState = (enrolledIn) => {
    if (Object.keys(enrolledIn).length === 0) {
      return
    }

    // Collect the data structures we need for our hooks
    const [apiParams, entityParams, url] = getEnrollmentList(enrolledIn)
    const newUrlParams = Object.assign(url, urlParams)

    // Collect and transform data for api params in localstorage to have the same structure as enrolledIn
    // or return an empty array
    const storageArray = storage.apiParams
      ? storage.apiParams
          .split('|')
          .filter((n) => n !== '')
          .map((n) => n.split('/')[1])
      : []

    // if localStorage is empty, we update parameters and then return early
    if (storageArray.length === 0) {
      setStorage({ urlParams: newUrlParams, apiParams, entityParams })
      addApiParams(apiParams, entityParams)
      updateUrlParams(newUrlParams)
      return
    }

    // This check should only be done if storageArray has data, we check for a diff
    // between localStorage api params and enrolledIn data, if it fails we update params
    if (Object.keys(enrolledIn).every((v) => storageArray.includes(v))) {
      return
    }
    setStorage({ urlParams: newUrlParams, apiParams, entityParams })
    addApiParams(apiParams, entityParams)
    updateUrlParams(newUrlParams)
  }

  const {
    documents = [],
    quicklinks = [],
    facets = [],
    enrolledIn = {},
    orgNames = [],
    orgTypes = [],
  } = useSearch()

  setInitialState(enrolledIn)

  const quicklinksWithType = quicklinks.map((item) => ({
    ...item,
    type: '__quicklink__',
  }))

  const quicklinksAndDocuments = quicklinksWithType.concat(resultList)

  useScrollPosition(documents)

  const handleClick = () => {
    toggleDiv()
  }

  return (
    <>
      {urlParamsChanged && (
        <Message message={t('info_choices_changed')} standard />
      )}
      <FilterBar>
        <UtilityBar
          modifierClasses="utility-bar--filter u-align-center"
          handleClick={handleClick}
          showUtilityBar={showUtilityBar}
          setUtilityBar={setUtilityBar}
        >
          <Label
            text={t('show_information_from')}
            modifierClasses="u-text-lg u-pr-100"
          />
          <LabelBadges />
          <div>
            <ContentToggle
              label={t('change')}
              handleClick={handleClick}
              toggled={showUtilityBar}
            />
          </div>
        </UtilityBar>
        {showDiv && (
          <Facets
            facets={facets}
            enrolledIn={enrolledIn}
            orgNames={orgNames}
            orgTypes={orgTypes}
          />
        )}

        <>
          <InPageNavPortal />
          <LocalContentPortal quicklinksAndDocuments={quicklinksAndDocuments} />
        </>
      </FilterBar>
    </>
  )
}

export default LocalPageTemplate
